/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { computeTrendProjection } from '@/lib/analytics/trendProjection';
import { TrendProjection } from '@/lib/analytics/types';

/** Returns the number of days in a given month (1-indexed). */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function useTrendProjection() {
  const { user } = useAuth();

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-indexed

  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const todayStr = today.toISOString().slice(0, 10);

  const totalDays = daysInMonth(year, month);
  // daysElapsed = day-of-month (1 on the 1st, so at least 1)
  const daysElapsed = today.getDate();

  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const totalDaysInYear = (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 366 : 365;
  const dayOfYear = Math.ceil(
    (today.getTime() - new Date(year, 0, 1).getTime()) / 86400000
  ) + 1;

  return useQuery({
    queryKey: ['trend-projection', user?.id, todayStr],
    queryFn: async (): Promise<TrendProjection> => {
      const hasInsufficientData = daysElapsed < 3;

      if (hasInsufficientData) {
        return { projectedMonthEnd: 0, projectedYearEnd: 0, hasInsufficientData: true };
      }

      // Fetch current month expenses
      const { data: monthTx, error: monthErr } = await (supabase as any)
        .from('transactions')
        .select('amount, currency')
        .eq('user_id', user!.id)
        .eq('type', 'expense')
        .gte('transaction_date', monthStart)
        .lte('transaction_date', todayStr);

      if (monthErr) throw monthErr;

      const totalSpentMonth = (monthTx ?? []).reduce((sum: number, t: any) => sum + t.amount, 0);

      // Fetch current year expenses
      const { data: yearTx, error: yearErr } = await (supabase as any)
        .from('transactions')
        .select('amount, currency')
        .eq('user_id', user!.id)
        .eq('type', 'expense')
        .gte('transaction_date', yearStart)
        .lte('transaction_date', todayStr);

      if (yearErr) throw yearErr;

      const totalSpentYear = (yearTx ?? []).reduce((sum: number, t: any) => sum + t.amount, 0);

      return {
        projectedMonthEnd: computeTrendProjection(totalSpentMonth, daysElapsed, totalDays),
        projectedYearEnd: computeTrendProjection(totalSpentYear, dayOfYear, totalDaysInYear),
        hasInsufficientData: false,
      };
    },
    enabled: !!user,
  });
}
